import type { PipelineTestReport, SkillTestReport } from './harnessDisplay';

export type OutputNode = {
  id: string;
  name: string;
  kind: 'file' | 'folder';
  path?: string;
  size?: string;
  lang?: string;
  content?: string;
  children?: OutputNode[];
};

export type ViewerKind = 'image' | 'pdf' | 'html' | 'code' | 'text' | 'preview' | 'markdown' | 'table';

export const IFRAME_SANDBOX = 'allow-scripts allow-forms';

export function viewerKind(name: string, lang?: string): ViewerKind {
  if (name === 'preview.json' || lang === 'preview') return 'preview';
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
  if (lang === 'image' || ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'].includes(ext)) return 'image';
  if (lang === 'pdf' || ext === '.pdf') return 'pdf';
  if (lang === 'html' || ext === '.html' || ext === '.htm') return 'html';
  if (lang === 'csv' || ext === '.csv') return 'table';
  if (lang === 'md' || ext === '.md' || ext === '.markdown') return 'markdown';
  if (['ts', 'tsx', 'js', 'jsx', 'css', 'json', 'py', 'go', 'rs'].includes(lang || '') ||
    ['.ts', '.tsx', '.js', '.jsx', '.css', '.json', '.jsonl', '.py', '.go', '.rs'].includes(ext)) {
    return 'code';
  }
  return 'text';
}

export function primaryArtifact(tree: OutputNode[]): OutputNode | null {
  const files = flattenFiles(tree);
  const preview = files.find((f) => f.name === 'preview.json');
  if (preview) return preview;
  const outputHtml = files.find((f) => f.name === 'output.html');
  if (outputHtml) return outputHtml;
  return files.find((f) => f.name.endsWith('.html')) ?? null;
}

export function parsePreviewUrl(content: string | undefined): string | null {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content) as { url?: string };
    const url = (parsed.url || '').trim();
    return url || null;
  } catch {
    return null;
  }
}

export function artifactFileUrl(outcomeId: string, path: string): string {
  return `/api/outcomes/${outcomeId}/artifacts/file?path=${encodeURIComponent(path)}`;
}

export function artifactsZipUrl(outcomeId: string): string {
  return `/api/outcomes/${outcomeId}/artifacts.zip`;
}

export function parseShareUrl(content: string | undefined): string | null {
  return parsePreviewUrl(content);
}

export function shareUrlFromTree(tree: OutputNode[]): string | null {
  const file = flattenFiles(tree).find((f) => f.name === 'share.json');
  return parseShareUrl(file?.content);
}

export function hasInfraArtifacts(tree: OutputNode[]): boolean {
  return flattenFiles(tree).some((f) => {
    const hay = `${f.path || ''} ${f.id} ${f.name} ${f.content || ''}`.replace(/\\/g, '/');
    return /(^|\/)infra(\/|$)/.test(hay) || /\.tf$/.test(f.name) || /aws-terraform/.test(hay) || /"infra\//.test(f.content || '');
  });
}

const RESERVED_SLUGS = new Set(['www', 'mail', 'api', 'desktop', 'reports', 'admin', 'app', 'staging']);

export function deploySlug(raw: string | null | undefined): string {
  const s = (raw || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  if (!s || RESERVED_SLUGS.has(s)) return 'site';
  return s;
}

export function deployHost(raw: string | null | undefined): string {
  return `${deploySlug(raw)}.sourcetape.io`;
}

export async function downloadAllArtifacts(
  outcomeId: string,
  fetchFn: (input: string) => Promise<Response>,
  save: (blob: Blob, name: string) => void,
): Promise<void> {
  const res = await fetchFn(artifactsZipUrl(outcomeId));
  if (!res.ok) throw new Error('download failed');
  const blob = await res.blob();
  save(blob, 'artifacts.zip');
}

const TABLE_ROW_CAP = 500;

export function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let i = 0;
  let inQuotes = false;
  const s = text.replace(/^\uFEFF/, '');
  while (i < s.length) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ',') {
      row.push(cell);
      cell = '';
      i += 1;
      continue;
    }
    if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i += 1;
      row.push(cell);
      cell = '';
      if (row.some((c) => c.length > 0) || rows.length === 0) rows.push(row);
      row = [];
      i += 1;
      if (rows.length >= TABLE_ROW_CAP + 1) break;
      continue;
    }
    cell += ch;
    i += 1;
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows.slice(0, TABLE_ROW_CAP + 1);
}

export function flattenFiles(nodes: OutputNode[]): OutputNode[] {
  const out: OutputNode[] = [];
  const walk = (list: OutputNode[]) => {
    for (const n of list) {
      if (n.kind === 'file') out.push(n);
      else if (n.children) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

export function artifactsForRun(tree: OutputNode[], runId: string | null | undefined): OutputNode[] {
  const needle = (runId || '').trim();
  if (!needle) return [];
  const out: OutputNode[] = [];
  const walk = (nodes: OutputNode[] | undefined, trail: string[]) => {
    for (const n of nodes || []) {
      const path = trail.concat(n.name || '');
      if (n.kind === 'file' && path.some((p) => p.includes(needle))) out.push(n);
      if (n.children) walk(n.children, path);
    }
  };
  walk(tree, []);
  return out;
}

export function findOutputNode(nodes: OutputNode[], id: string): OutputNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) {
      const nested = findOutputNode(n.children, id);
      if (nested) return nested;
    }
  }
  return null;
}

export function buildTestOutputTree(
  report: PipelineTestReport | null | undefined,
  pipelineName: string,
): OutputNode[] {
  if (!report) return [];

  const children: OutputNode[] = [
    fileNode('summary.md', buildSummaryMd(report, pipelineName), 'md'),
    fileNode('report.json', prettyJSON(report), 'json'),
  ];

  const skillFiles = (report.skill_reports || []).map((s, i) =>
    fileNode(`${safeFileStem(s.skill_id)}.json`, prettyJSON(s), 'json', `skill-${i}`),
  );
  if (skillFiles.length) {
    children.push({ id: 'skills', name: 'skills', kind: 'folder', children: skillFiles });
  }

  if ((report.compatibility || []).length) {
    children.push(fileNode('compatibility.json', prettyJSON(report.compatibility), 'json'));
  }
  if ((report.novel_failures || []).length) {
    children.push(fileNode('novel-failures.json', prettyJSON(report.novel_failures), 'json'));
  }

  const rootName = (pipelineName || 'pipeline').replace(/\*$/, '').trim() || 'pipeline';
  return [{ id: 'root', name: rootName, kind: 'folder', children }];
}

function fileNode(name: string, content: string, lang: string, id = name): OutputNode {
  return { id, name, kind: 'file', lang, content, size: formatSize(content) };
}

function prettyJSON(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`;
}

function formatSize(content: string): string {
  const n = new TextEncoder().encode(content).length;
  if (n < 1024) return `${n} B`;
  return `${(n / 1024).toFixed(1)} KB`;
}

function safeFileStem(id: string): string {
  const stem = id.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return stem || 'skill';
}

function buildSummaryMd(report: PipelineTestReport, pipelineName: string): string {
  const title = (pipelineName || 'pipeline').replace(/\*$/, '').trim() || 'pipeline';
  const lines: string[] = [
    `# ${title} test report`,
    '',
    `**Score:** ${report.score}/12`,
    `**Success:** ${report.success ? 'yes' : 'no'}`,
    '',
    '## Skill reports',
  ];
  const skills = report.skill_reports || [];
  if (!skills.length) {
    lines.push('- none');
  } else {
    for (const s of skills) {
      lines.push(`- ${s.skill_id}: ${skillLine(s)}`);
      for (const r of s.results || []) {
        const reason = r.reason ? ` — ${r.reason}` : '';
        lines.push(`  - ${r.case_id}: ${r.status}${reason}`);
      }
    }
  }
  const compat = report.compatibility || [];
  if (compat.length) {
    lines.push('', '## Compatibility');
    for (const c of compat) {
      const reason = c.reason ? ` — ${c.reason}` : '';
      lines.push(`- ${c.edge_id || '?'} ${c.from} -> ${c.to} ${c.field}: ${c.status}${reason}`);
    }
  }
  const novel = report.novel_failures || [];
  if (novel.length) {
    lines.push('', '## Novel failures');
    for (const n of novel) {
      lines.push(`- ${n.from_skill} -> ${n.to_skill} ${n.field}: ${n.reason || ''}`);
    }
  }
  lines.push('');
  return lines.join('\n');
}

function skillLine(s: SkillTestReport): string {
  const mark = s.success ? 'passed' : 'failed';
  if (typeof s.passed === 'number' && typeof s.total === 'number') {
    return `${mark} (${s.passed}/${s.total})`;
  }
  if (typeof s.score === 'number') return `${mark} (${s.score})`;
  return mark;
}
